/**
 * Projekt-spezifische API-Funktionen
 * CRUD-Operationen für Windpark-Projekte
 */
import { api, getAuthHeaders } from "./client";
import config from "../../config/environment";
import {
  Windpark,
  AktiverErtragswert,
  KPIData,
  ProjectOverviewItem,
  ProjectOverviewResponse,
  ProjectLaufzeitResponse,
  ProjectAktiverErtragswertResponse,
  ValidationsResponse,
  PlanOptionsResponse,
  Gesamtstatus,
} from "../../types";

export const projectsApi = {
  /**
   * @deprecated Wird nicht mehr benötigt – KPIs werden jetzt über getProjectsOverview() mitgeliefert.
   * Holt die aggregierten Dashboard-KPIs
   */
  async getProjectKpis(): Promise<KPIData> {
    const response = await api.get("/projects/kpis");
    return {
      anzahlProjekte: response.anzahl_projekte,
      durchschnittlicherIRR: response.durchschnittlicher_irr,
      durchschnittlicheEkQuote: response.durchschnittliche_ek_quote,
      wirtschaftlichkeitErklaerung: "",
      reifegradErklaerung: "",
      reifegradGewichte: null,
    };
  },

  /**
   * Holt alle Projekte mit berechneten Kennzahlen für die Übersichtstabelle
   * sowie die aggregierten Dashboard-KPIs (Ø IRR, Ø EK-Quote, Anzahl Projekte).
   *
   * Dieser Endpoint ersetzt den separaten getProjectKpis()-Aufruf.
   * Die KPIs werden direkt aus den berechneten Übersichtsdaten abgeleitet,
   * wodurch eine doppelte Cashflow-Berechnung vermieden wird.
   */
  async getProjectsOverview(): Promise<{
    projects: ProjectOverviewItem[];
    kpis: KPIData;
  }> {
    const response: ProjectOverviewResponse =
      await api.get("/projects/overview");
    return {
      projects: response.projects,
      kpis: {
        anzahlProjekte: response.anzahl_projekte,
        durchschnittlicherIRR: response.durchschnittlicher_irr,
        durchschnittlicheEkQuote: response.durchschnittliche_ek_quote,
        wirtschaftlichkeitErklaerung: response.wirtschaftlichkeit_erklaerung,
        reifegradErklaerung: response.reifegrad_erklaerung,
        reifegradGewichte: response.reifegrad_gewichte,
      },
    };
  },

  async getProjects(): Promise<any[]> {
    return api.get("/projects");
  },

  async getProject(projectId: string): Promise<any> {
    return api.get(`/projects/${projectId}`);
  },

  /** Gesamtstatus aus den sechs Domänen-Status – der Server leitet ihn ab, nicht das Frontend */
  async getGesamtstatus(projectId: string): Promise<Gesamtstatus> {
    return api.get(`/projects/${projectId}/gesamtstatus`);
  },

  async createProject(projectData: any): Promise<any> {
    return api.post("/projects", projectData);
  },

  async updateProject(projectId: string, projectData: any): Promise<any> {
    return api.put(`/projects/${projectId}`, projectData);
  },

  async updateProjectPartial(
    projectId: string,
    updates: Partial<any>,
  ): Promise<any> {
    return api.patch(`/projects/${projectId}`, updates);
  },

  async deleteProject(projectId: string): Promise<any> {
    return api.delete(`/projects/${projectId}`);
  },

  /**
   * Generische Fetch-Funktion (Wrapper für GET)
   */
  async fetch(endpoint: string, options: RequestInit = {}): Promise<any> {
    return api.get(endpoint, options);
  },

  /**
   * Ändert den aktiven Ertragswert und gibt Validierungen zurück.
   *
   * Dies ist ein dedizierter Action-Endpoint der:
   * 1. Nur den aktiven Ertragswert ändert
   * 2. Domänenübergreifende Validierungen durchführt (z.B. PPA-Mengen vs. Jahresertrag)
   * 3. Neuberechnung von Energieerlösen und GuV triggert
   *
   * Die Validierungen ermöglichen dem Frontend, Warnungen auf anderen Pages
   * anzuzeigen (z.B. bei Tarifen, wenn PPA-Abnahmemengen den Ertrag übersteigen).
   */
  async setAktiverErtragswert(
    projectId: string,
    ertragswert: AktiverErtragswert,
  ): Promise<ProjectAktiverErtragswertResponse> {
    return api.put(`/projects/${projectId}/aktiver-ertragswert`, {
      aktiver_ertragswert: ertragswert,
    });
  },

  /**
   * Ändert die Projektlaufzeit und gibt Validierungen zurück.
   *
   * Dies ist ein dedizierter Action-Endpoint der:
   * 1. Nur die Projektlaufzeit ändert
   * 2. Domänenübergreifende Validierungen durchführt (z.B. PPA-Zeiträume)
   * 3. Neuberechnung von Energieerlösen und GuV triggert
   *
   * Die Validierungen ermöglichen dem Frontend, Warnungen auf anderen Pages
   * anzuzeigen (z.B. bei Tarifen, wenn PPAs außerhalb der Laufzeit liegen).
   */
  async updateProjectLaufzeit(
    projectId: string,
    laufzeitJahre: number,
  ): Promise<ProjectLaufzeitResponse> {
    return api.put(`/projects/${projectId}/laufzeit`, {
      laufzeit_jahre: laufzeitJahre,
    });
  },

  /**
   * Holt alle aktuellen Validierungen für ein Projekt.
   *
   * Dieser Endpoint wird beim Laden eines Projekts aufgerufen, um
   * domänenübergreifende Validierungen zu laden (z.B. nach Page Reload).
   *
   * Die Validierungen werden zentral aus allen relevanten Domänen gesammelt:
   * - Tarife: PPA-Zeiträume, PPA-Mengen vs. Jahresertrag
   * - (Erweiterbar für: Kosten, Finanzierung, etc.)
   */
  async getProjectValidations(projectId: string): Promise<ValidationsResponse> {
    return api.get(`/projects/${projectId}/validations`);
  },

  /**
   * Holt die verfügbaren Auswahlwerte für planungsrechtliche Risikokennzahlen.
   * Wird einmalig beim Öffnen der Projektrahmen-Seite geladen.
   */
  async getPlanOptions(): Promise<PlanOptionsResponse> {
    return api.get("/projects/plan-options");
  },

  /**
   * Holt die verfügbaren Auswahlwerte für die projektweiten Domänen-Status
   * (Planungsrecht, BImSchG, EEG-Ausschreibung, Bau). Wird beim Öffnen der Übersicht-Seite geladen.
   */
  async getStatusOptions(): Promise<{
    planungsrecht_status_options: string[];
    bimschg_status_options: string[];
    eeg_ausschreibung_status_options: string[];
    bau_status_options: string[];
  }> {
    return api.get("/projects/status-options");
  },

  /**
   * Erstellt einen vollständigen Excel-Export des Projekts und löst den
   * Browser-Download aus. Die Tabellenblätter folgen der Frontend-Navigation.
   */
  async exportProject(projectId: string): Promise<void> {
    const headers = await getAuthHeaders();
    // Content-Type für GET nicht nötig
    delete (headers as any)["Content-Type"];

    const response = await fetch(
      `${config.apiBaseUrl}/projects/${projectId}/export`,
      {
        method: "GET",
        headers,
      },
    );

    if (!response.ok) {
      throw new Error(
        `Export fehlgeschlagen: ${response.status} ${response.statusText}`,
      );
    }

    const blob = await response.blob();
    const contentDisposition =
      response.headers.get("Content-Disposition") || "";
    const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch ? filenameMatch[1] : "export.xlsx";

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },
};
